import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { collection, deleteDoc, doc, onSnapshot, query, updateDoc, setDoc } from "firebase/firestore";
import { sendPasswordResetEmail } from "firebase/auth";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, auth, storage, saveLog } from "../lib/firebase";

import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import Navbar from "../components/ui/Navbar"; 
import AdminBar from "../components/ui/adminbar";
import { toast } from "sonner";

import {
  Users, Search, Save, Ban, Trash2, Mail,
  Phone, MapPin, Lock, KeyRound,
  Hash, Fingerprint, Loader2, User, Camera, XCircle, ChevronLeft, ChevronRight, UserCog
} from "lucide-react";

export default function AdminClients() {
  const navigate = useNavigate();
  const { userProfile, authLoading } = useAuth();
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedClient, setSelectedClient] = useState(null);
  const [users, setUsers] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [validatingCep, setValidatingCep] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  // --- VALIDADORES MATEMÁTICOS (PRESERVADOS) ---
  const validateCPF = (cpf) => {
    cpf = cpf.replace(/[^\d]+/g, '');
    if (cpf.length !== 11 || !!cpf.match(/(\d)\1{10}/)) return false;
    let add = 0;
    for (let i = 0; i < 9; i++) add += parseInt(cpf.charAt(i)) * (10 - i);
    let rev = 11 - (add % 11);
    if (rev === 10 || rev === 11) rev = 0;
    if (rev !== parseInt(cpf.charAt(9))) return false;
    add = 0;
    for (let i = 0; i < 10; i++) add += parseInt(cpf.charAt(i)) * (11 - i);
    rev = 11 - (add % 11);
    if (rev === 10 || rev === 11) rev = 0;
    return rev === parseInt(cpf.charAt(10));
  };

  const validateCNPJ = (cnpj) => {
    cnpj = cnpj.replace(/[^\d]+/g, '');
    if (cnpj.length !== 14 || !!cnpj.match(/(\d)\1{13}/)) return false;
    let size = cnpj.length - 2;
    let numbers = cnpj.substring(0, size);
    const digits = cnpj.substring(size);
    let sum = 0;
    let pos = size - 7;
    for (let i = size; i >= 1; i--) {
      sum += numbers.charAt(size - i) * pos--;
      if (pos < 2) pos = 9;
    }
    let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
    if (result !== parseInt(digits.charAt(0))) return false;
    size = size + 1;
    numbers = cnpj.substring(0, size);
    sum = 0;
    pos = size - 7;
    for (let i = size; i >= 1; i--) {
      sum += numbers.charAt(size - i) * pos--;
      if (pos < 2) pos = 9;
    }
    result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
    return result === parseInt(digits.charAt(1));
  };

  const onlyNumbers = (val = "") => String(val || "").replace(/\D/g, "");

  const formatCpfCnpj = (val = "") => {
    let v = onlyNumbers(val).slice(0, 14);
    if (v.length <= 11) {
      return v.replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d{1,2})$/, "$1-$2");
    }
    return v.replace(/^(\d{2})(\d)/, "$1.$2").replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3").replace(/\.(\d{3})(\d)/, ".$1/$2").replace(/(\d{4})(\d)/, "$1-$2");
  };

  const formatPhone = (val = "") => {
    let v = onlyNumbers(val).slice(0, 11);
    if (v.length > 10) return v.replace(/^(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
    if (v.length > 5) return v.replace(/^(\d{2})(\d{4})(\d{0,4})/, "($1) $2-$3");
    if (v.length > 2) return v.replace(/^(\d{2})(\d{0,5})/, "($1) $2");
    return v.length > 0 ? "(" + v : v;
  };

  // --- ESCUTA LIVE ---
  useEffect(() => {
    const q = query(collection(db, "users"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setUsers(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // --- VIA CEP ---
  const handleCepBlur = async (id, cep) => {
    const cleanCep = onlyNumbers(cep);
    if (cleanCep.length !== 8) return;
    setValidatingCep(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
      const data = await res.json();
      if (!data.erro) {
        setUsers(prev => prev.map(u => {
          if (u.id === id) {
            return {
              ...u,
              addressDetails: {
                ...(u.addressDetails || {}),
                cep: cleanCep,
                street: data.logradouro.toUpperCase(),
                neighborhood: data.bairro.toUpperCase(),
                city: data.localidade.toUpperCase()
              }
            };
          }
          return u;
        }));
      }
    } finally { setValidatingCep(false); }
  };

  // --- GESTÃO DE FOTOS ---
  const handlePhotoUpload = async (clientId, file) => {
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const storageRef = ref(storage, `users/${clientId}/profile.jpg`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      await updateDoc(doc(db, "users", clientId), { photoURL: url });
      await saveLog(userProfile, "UPLOAD FOTO", `Admin alterou foto do cliente ${clientId}`);
      toast.success("Foto atualizada!");
    } catch (e) { toast.error("Erro no upload."); } finally { setUploadingPhoto(false); }
  };

  const handleRemovePhoto = async (clientId, currentUrl) => {
    if (!window.confirm("Remover foto?")) return;
    setUploadingPhoto(true);
    try {
      if (currentUrl && currentUrl.includes("firebasestorage")) {
        await deleteObject(ref(storage, currentUrl)).catch(() => null);
      }
      await updateDoc(doc(db, "users", clientId), { photoURL: null });
      toast.success("Foto removida!");
    } catch (e) { toast.error("Erro ao remover."); } finally { setUploadingPhoto(false); }
  };

  // --- FILTRAGEM (LÓGICA EXPANDIDA COM FILTRO DE TÉCNICO) ---
  const filteredClients = useMemo(() => {
    const baseClients = users.filter(u => !u.isAdmin);
    const term = search.toLowerCase().trim();
    const termOnlyNumbers = onlyNumbers(term);

    return baseClients.filter(c => {
      const isBlocked = c.isBlocked || c.blocked || c.status === 'banned' || false;
      const isTechnician = c.userType?.toLowerCase() === 'technician' || c.isTechnician === true;

      // Filtro de Status + Filtro de Tipo (Técnico)
      const matchesStatus = 
        statusFilter === "all" ? true : 
        statusFilter === "blocked" ? isBlocked : 
        statusFilter === "technician" ? isTechnician : !isBlocked;

      if (!matchesStatus) return false;
      if (!term) return true;
      return (c.name || "").toLowerCase().includes(term) || (c.email || "").toLowerCase().includes(term) || onlyNumbers(c.cpfCnpj).includes(termOnlyNumbers) || onlyNumbers(c.phone).includes(termOnlyNumbers);
    }).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [users, search, statusFilter]);

  const totalPages = Math.ceil(filteredClients.length / itemsPerPage);
  const paginatedClients = filteredClients.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handleUpdateField = (id, field, value, isAddress = false) => {
    setUsers(prev => prev.map(u => {
      if (u.id === id) {
        if (isAddress) return { ...u, addressDetails: { ...(u.addressDetails || {}), [field]: value } };
        return { ...u, [field]: value };
      }
      return u;
    }));
  };

  const handleSave = async (client) => {
    const docClean = onlyNumbers(client.cpfCnpj);
    if (docClean.length > 0) {
      if (docClean.length <= 11 && !validateCPF(docClean)) return toast.error("CPF Inválido!");
      if (docClean.length > 11 && !validateCNPJ(docClean)) return toast.error("CNPJ Inválido!");
    }
    try {
      await updateDoc(doc(db, "users", client.id), { ...client, updatedAt: new Date().toISOString() });
      await saveLog(userProfile, "ATUALIZAÇÃO CLIENTE", `Admin alterou dados de ${client.name}.`);
      toast.success("Sincronizado!");
    } catch (e) { toast.error("Erro ao salvar."); }
  };

  const handleBlockToggle = async (client) => {
    // Agora verifica o status banned também para garantir
    const isBlocked = client.isBlocked || client.blocked || client.status === 'banned' || false;
    const newStatus = !isBlocked;
    let reason = "";
    if (newStatus) {
      reason = window.prompt("MOTIVO DO BANIMENTO (BLACK-LIST):");
      if (reason === null || !reason.trim()) return toast.error("Motivo obrigatório.");
    }
    try {
      const userRef = doc(db, "users", client.id);
      const blacklistRef = doc(db, "blacklist", client.email.toLowerCase());
      if (newStatus) {
        // BANINDO: Adiciona a flag status: 'banned'
        await updateDoc(userRef, { isBlocked: true, blocked: true, blockReason: reason, status: 'banned' });
        await setDoc(blacklistRef, { email: client.email.toLowerCase(), reason, bannedAt: new Date().toISOString() });
        await saveLog(userProfile, "BANIMENTO", `Cliente ${client.name} banido.`);
      } else {
        // DESBANINDO: CORREÇÃO APLICADA AQUI - Volta o status para 'active' para que ele consiga logar!
        await updateDoc(userRef, { isBlocked: false, blocked: false, blockReason: "", status: 'active' });
        await deleteDoc(blacklistRef);
        await saveLog(userProfile, "LIBERAÇÃO", `Cliente ${client.name} liberado.`);
      }
      toast.success(newStatus ? "BANIDO" : "LIBERADO");
    } catch (e) { toast.error("Erro."); }
  };

  if (authLoading) return <div className="h-screen flex items-center justify-center font-black text-slate-400">CARREGANDO...</div>;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <div className="flex pt-[73px]">
        <AdminBar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

        <main className="flex-1 p-6 lg:p-10 max-w-7xl mx-auto w-full text-left">
          <header className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <h1 className="text-4xl font-black text-slate-900 uppercase italic tracking-tighter text-left">Clientes <span className="text-blue-600">Gerais</span></h1>
              <div className="flex items-center gap-2 mt-4 flex-wrap">
                 <button onClick={() => {setStatusFilter("all"); setCurrentPage(1);}} className={`text-[10px] font-black uppercase px-4 py-2 rounded-full transition-all ${statusFilter === 'all' ? 'bg-slate-900 text-white' : 'bg-white text-slate-400 border border-slate-100'}`}>Todos</button>
                 <button onClick={() => {setStatusFilter("active"); setCurrentPage(1);}} className={`text-[10px] font-black uppercase px-4 py-2 rounded-full transition-all ${statusFilter === 'active' ? 'bg-green-600 text-white' : 'bg-white text-slate-400 border border-slate-100'}`}>Ativos</button>
                 <button onClick={() => {setStatusFilter("technician"); setCurrentPage(1);}} className={`text-[10px] font-black uppercase px-4 py-2 rounded-full transition-all ${statusFilter === 'technician' ? 'bg-orange-500 text-white' : 'bg-white text-slate-400 border border-slate-100'}`}>Profissionais</button>
                 <button onClick={() => {setStatusFilter("blocked"); setCurrentPage(1);}} className={`text-[10px] font-black uppercase px-4 py-2 rounded-full transition-all ${statusFilter === 'blocked' ? 'bg-red-600 text-white' : 'bg-white text-slate-400 border border-slate-100'}`}>Banidos</button>
              </div>
            </div>
            <div className="relative w-full md:w-96 group text-left">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={20} />
              <input type="text" placeholder="BUSCAR NOME, E-MAIL OU CPF..." className="w-full h-14 pl-12 pr-6 bg-white border-none shadow-xl rounded-2xl font-bold text-sm uppercase italic outline-none focus:ring-2 focus:ring-blue-500/20" value={search} onChange={(e) => {setSearch(e.target.value); setCurrentPage(1);}} />
            </div>
          </header>

          <div className="grid gap-4">
            {loading ? <div className="py-20 text-center font-black text-slate-300 animate-pulse uppercase">Sincronizando...</div> : paginatedClients.map((client) => {
              const isBlocked = client.isBlocked || client.blocked || client.status === 'banned';
              const isTech = client.userType?.toLowerCase() === 'technician' || client.isTechnician === true;
              const isOpen = selectedClient === client.id;
              return (
                <Card key={client.id} className={`rounded-[2rem] border-none shadow-xl transition-all ${isBlocked ? 'border-l-8 border-red-500 bg-red-50/20' : isTech ? 'border-l-8 border-orange-500' : 'border-l-8 border-transparent'}`}>
                  <CardContent className="p-0">
                    <div className="p-6 flex flex-col md:flex-row items-center justify-between gap-6 text-left">
                      <div className="flex items-center gap-5">
                        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center overflow-hidden relative group bg-slate-100`}>
                          {client.photoURL ? <img src={client.photoURL} className="w-full h-full object-cover" alt="" /> : isTech ? <UserCog size={24} className="text-orange-500"/> : <User size={24} className="text-slate-300"/>}
                          {uploadingPhoto && selectedClient === client.id && <div className="absolute inset-0 bg-black/40 flex items-center justify-center"><Loader2 className="animate-spin text-white" size={16}/></div>}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-xl font-black text-slate-900 uppercase italic tracking-tighter">{client.name}</h3>
                            {isTech && <Badge className="bg-orange-100 text-orange-600 border-none font-black text-[8px] uppercase px-2 py-0.5 tracking-tighter">TÉCNICO</Badge>}
                          </div>
                          <p className="text-xs font-bold text-slate-400 uppercase italic tracking-tighter">{client.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Button onClick={() => setSelectedClient(isOpen ? null : client.id)} variant="ghost" className="h-12 rounded-xl font-black uppercase text-[10px] bg-slate-100">{isOpen ? "Fechar" : "Gerenciar"}</Button>
                        <Button onClick={() => handleBlockToggle(client)} className={`h-12 px-5 rounded-xl font-black uppercase italic text-[10px] gap-2 ${isBlocked ? 'bg-green-500 text-white shadow-md' : 'bg-red-100 text-red-600 hover:bg-red-600 hover:text-white transition-colors'}`}>{isBlocked ? "Liberar" : "Banir"}</Button>
                      </div>
                    </div>

                    {isOpen && (
                      <div className="p-8 bg-white border-t border-slate-100 animate-in slide-in-from-top-2 text-left">
                        <div className="mb-8 p-4 bg-slate-50 rounded-3xl border border-slate-100 flex flex-col md:flex-row items-center gap-6 text-left">
                            <div className="w-24 h-24 rounded-3xl bg-white overflow-hidden flex items-center justify-center border-4 border-white shadow-xl relative group">
                                {client.photoURL ? <img src={client.photoURL} className="w-full h-full object-cover" alt="" /> : <User size={32} className="text-slate-200" />}
                                <label className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-all flex flex-col items-center justify-center cursor-pointer text-white text-[8px] font-black uppercase text-center">
                                    <Camera size={16} className="mb-1" /> Alterar
                                    <input type="file" className="hidden" accept="image/*" onChange={(e) => handlePhotoUpload(client.id, e.target.files[0])} />
                                </label>
                            </div>
                            <div className="space-y-3 flex-1 text-left">
                                <Label className="text-[10px] font-black uppercase text-slate-500">Avatar do Usuário</Label>
                                <div className="flex gap-2">
                                    {client.photoURL && <Button onClick={() => handleRemovePhoto(client.id, client.photoURL)} disabled={uploadingPhoto} variant="destructive" className="h-9 rounded-lg font-black uppercase text-[9px] gap-2 px-4 shadow-md"><XCircle size={14}/> Remover Foto</Button>}
                                    <Button onClick={() => sendPasswordResetEmail(auth, client.email).then(() => toast.success("Reset enviado!"))} variant="outline" className="h-9 rounded-lg font-black uppercase text-[9px] italic border-slate-200 gap-2 hover:bg-slate-900 hover:text-white transition-all bg-white"><KeyRound size={14} /> Resetar Acesso</Button>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
                          <div className="space-y-1"><Label className="text-[10px] font-black uppercase text-blue-600 ml-1">Nome Completo</Label><input className="w-full h-12 rounded-xl border border-slate-100 bg-slate-50 px-4 font-bold outline-none" value={client.name || ""} onChange={(e) => handleUpdateField(client.id, "name", e.target.value)} /></div>
                          <div className="space-y-1"><Label className="text-[10px] font-black uppercase text-blue-600 ml-1">CPF ou CNPJ</Label><input className="w-full h-12 rounded-xl border border-slate-100 bg-slate-50 px-4 font-bold outline-none" value={client.cpfCnpj || ""} onChange={(e) => handleUpdateField(client.id, "cpfCnpj", formatCpfCnpj(e.target.value))} /></div>
                          <div className="space-y-1"><Label className="text-[10px] font-black uppercase text-blue-600 ml-1">WhatsApp</Label><input className="w-full h-12 rounded-xl border border-slate-100 bg-slate-50 px-4 font-bold outline-none" value={client.phone || ""} onChange={(e) => handleUpdateField(client.id, "phone", formatPhone(e.target.value))} /></div>

                          <div className="md:col-span-3 flex items-center gap-2 mt-4 border-b pb-2"><MapPin size={16} className="text-orange-600" /><h4 className="text-[10px] font-black uppercase text-slate-400 italic">Endereço</h4></div>
                          <div className="relative space-y-1">
                            <Label className="text-[10px] font-black uppercase text-slate-400 ml-1">CEP</Label>
                            <input className="w-full h-12 rounded-xl border border-slate-100 bg-slate-50 px-4 font-bold text-center outline-none focus:ring-2 ring-blue-500/20" value={client.addressDetails?.cep || ""} onBlur={(e) => handleCepBlur(client.id, e.target.value)} onChange={(e) => handleUpdateField(client.id, "cep", e.target.value, true)} />
                            {validatingCep && <Loader2 className="absolute right-3 top-9 animate-spin text-blue-500" size={14} />}
                          </div>
                          <div className="md:col-span-2 space-y-1"><Label className="text-[10px] font-black uppercase text-slate-400 ml-1">Rua / Logradouro</Label><input className="w-full h-12 rounded-xl border border-slate-100 bg-slate-50 px-4 font-bold outline-none" value={client.addressDetails?.street || ""} onChange={(e) => handleUpdateField(client.id, "street", e.target.value, true)} /></div>
                          <div className="space-y-1"><Label className="text-[10px] font-black uppercase text-slate-400 ml-1">Número</Label><input className="w-full h-12 rounded-xl border border-slate-100 bg-slate-50 px-4 font-bold text-center outline-none" value={client.addressDetails?.number || ""} onChange={(e) => handleUpdateField(client.id, "number", e.target.value, true)} /></div>
                          <div className="space-y-1"><Label className="text-[10px] font-black uppercase text-slate-300 ml-1">Bairro</Label><input readOnly className="w-full h-12 rounded-xl bg-slate-100 border-none px-4 font-bold text-slate-400" value={client.addressDetails?.neighborhood || ""} /></div>
                          <div className="space-y-1"><Label className="text-[10px] font-black uppercase text-slate-300 ml-1">Cidade</Label><input readOnly className="w-full h-12 rounded-xl bg-slate-100 border-none font-black italic text-blue-900/40" value={client.addressDetails?.city || ""} /></div>
                        </div>

                        <div className="flex justify-end gap-3 pt-10 border-t mt-8">
                          <Button onClick={() => handleSave(client)} className="h-14 px-10 bg-blue-600 text-white font-black rounded-2xl uppercase italic text-[11px] gap-3 shadow-lg"><Save size={18} /> ATUALIZAR LIVE</Button>
                          <Button variant="destructive" onClick={() => { if(window.confirm("Excluir conta?")) deleteDoc(doc(db, "users", client.id)) }} className="h-14 w-14 rounded-2xl shadow-lg active:scale-95 transition-all"><Trash2 size={20} /></Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="mt-12 flex items-center justify-center gap-4">
                <Button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} variant="outline" className="h-12 w-12 rounded-xl"><ChevronLeft size={20}/></Button>
                <span className="font-black text-slate-400 uppercase italic text-xs">Página {currentPage} de {totalPages}</span>
                <Button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} variant="outline" className="h-12 w-12 rounded-xl"><ChevronRight size={20}/></Button>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}